// useLibrarySection.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  listLibraryEntries,
  normalizeLibraryTermKey,
  provisionLibraryAddon,
} from 'services/library/LibraryService';

function getFieldKey(field) {
  return String(field?.name || field?.key || '').trim();
}

function getDefaultSelectedFieldKeys(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map((field) => ({
      fieldKey: getFieldKey(field),
      includeByDefault: field?.includeByDefault !== false,
    }))
    .filter((entry) => entry.fieldKey)
    .filter((entry) => entry.includeByDefault)
    .map((entry) => entry.fieldKey);
}

function buildProvisionSuccessMessage(created, selectedFieldKeys) {
  const label = String(created?.label || 'Type').trim();
  const importedViewsCount = Number(created?.importedViewsCount || 0);
  const skippedViewsCount = Number(created?.skippedViewsCount || 0);
  const importedAutomationsCount = Number(created?.importedAutomationsCount || 0);
  const skippedAutomationsCount = Number(created?.skippedAutomationsCount || 0);
  const importedPageLayoutsCount = Number(created?.importedPageLayoutsCount || 0);
  const skippedPageLayoutsCount = Number(created?.skippedPageLayoutsCount || 0);
  const importedFieldsCount = Array.isArray(selectedFieldKeys) ? selectedFieldKeys.length : 0;
  const details = [
    `${importedFieldsCount} fields`,
    `${importedViewsCount} views${skippedViewsCount > 0 ? `, ${skippedViewsCount} skipped` : ''}`,
    `${importedAutomationsCount} automations${skippedAutomationsCount > 0 ? `, ${skippedAutomationsCount} skipped` : ''}`,
  ];

  if (importedPageLayoutsCount > 0) {
    details.push(`${importedPageLayoutsCount} page layout${importedPageLayoutsCount === 1 ? '' : 's'}${skippedPageLayoutsCount > 0 ? `, ${skippedPageLayoutsCount} skipped` : ''}`);
  }

  const suffix = importedPageLayoutsCount > 0
    ? ' Open any record to publish pages.'
    : '';

  return `Imported ${label}. Added ${details.join(' · ')}.${suffix}`;
}

export function useLibrarySection({
  isOpen,
  selectedTab,
  libraryTabValue,
  baseKey,
  availableTypes,
  onTypeSelect,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTermKey, setSelectedTermKey] = useState('');
  const [selectedAddonKey, setSelectedAddonKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisioningAddonKey, setProvisioningAddonKey] = useState('');
  const [selectedFieldKeys, setSelectedFieldKeys] = useState([]);

  const baseLabelSet = useMemo(() => {
    const labels = new Set();

    (availableTypes || []).forEach((typeDef) => {
      if (!typeDef || typeDef.system === true) return;
      const label = String(typeDef.label || '').trim();
      const key = normalizeLibraryTermKey(label);
      if (key) labels.add(key);
      const labelLower = String(typeDef.labelLower || '').trim().toLowerCase();
      if (labelLower) labels.add(normalizeLibraryTermKey(labelLower));
    });

    return labels;
  }, [availableTypes]);

  const normalizedQuery = String(query || '').trim().toLowerCase();

  const visibleTerms = useMemo(() => {
    return (terms || []).filter((term) => {
      const termKey = normalizeLibraryTermKey(term.key || term.label);
      if (!termKey) return false;
      if (baseLabelSet.has(termKey)) return false;

      if (!normalizedQuery) return true;
      const label = String(term.label || '').trim().toLowerCase();
      const pluralLabel = String(term.labelPlural || '').trim().toLowerCase();
      return label.includes(normalizedQuery)
        || pluralLabel.includes(normalizedQuery)
        || termKey.includes(normalizedQuery);
    });
  }, [normalizedQuery, terms, baseLabelSet]);

  const selectedTerm = useMemo(() => {
    if (!selectedTermKey) return null;
    return visibleTerms.find((term) => term.key === selectedTermKey) || null;
  }, [selectedTermKey, visibleTerms]);

  const selectedAddon = useMemo(() => {
    if (!selectedTerm || !selectedAddonKey) return null;
    return (selectedTerm.addons || []).find((addon) => addon.key === selectedAddonKey) || null;
  }, [selectedTerm, selectedAddonKey]);

  const isAddonStep = Boolean(selectedTerm && !selectedAddon);
  const isAddonPreviewStep = Boolean(selectedTerm && selectedAddon);

  const loadTerms = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const rows = await listLibraryEntries({ includeAddons: true });
      setTerms(rows);
    } catch (loadError) {
      setError(loadError?.message || 'Failed to load library entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || selectedTab !== libraryTabValue) return;
    loadTerms();
  }, [libraryTabValue, isOpen, loadTerms, selectedTab]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedTermKey('');
      setSelectedAddonKey('');
      setSelectedFieldKeys([]);
      setError('');
      setIsProvisioning(false);
      setProvisioningAddonKey('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTermKey) return;
    const exists = visibleTerms.some((term) => term.key === selectedTermKey);
    if (!exists) {
      setSelectedTermKey('');
      setSelectedAddonKey('');
    }
  }, [selectedTermKey, visibleTerms]);

  useEffect(() => {
    if (!selectedTerm || !selectedAddonKey) return;
    const exists = (selectedTerm.addons || []).some((addon) => addon.key === selectedAddonKey);
    if (!exists) setSelectedAddonKey('');
  }, [selectedTerm, selectedAddonKey]);

  useEffect(() => {
    const nextFields = selectedAddon?.schema?.fields || [];
    setSelectedFieldKeys(getDefaultSelectedFieldKeys(nextFields));
  }, [selectedAddon?.key, selectedAddon?.schema?.fields]);

  const handleProvisionAddon = useCallback(async (termKey, addonKey, nextSelectedFieldKeys = []) => {
    if (!baseKey || !termKey || !addonKey) return;

    setError('');
    setIsProvisioning(true);
    setProvisioningAddonKey(addonKey);

    try {
      const created = await provisionLibraryAddon({
        baseKey,
        termKey,
        addonKey,
        selectedFieldKeys: nextSelectedFieldKeys,
      });

      if (created?.key && typeof onTypeSelect === 'function') {
        onTypeSelect(created.key, created);
      }

      toast.success(buildProvisionSuccessMessage(created, nextSelectedFieldKeys));

      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (provisionError) {
      setError(provisionError?.message || 'Failed to provision library addon.');
      await loadTerms();
    } finally {
      setIsProvisioning(false);
      setProvisioningAddonKey('');
    }
  }, [loadTerms, onClose, onTypeSelect, baseKey]);

  const handleSelectTerm = useCallback((term) => {
    if (!term) return;

    const addons = Array.isArray(term.addons) ? term.addons : [];
    setSelectedTermKey(term.key);
    setSelectedAddonKey(addons.length === 1 ? addons[0]?.key || '' : '');
  }, []);

  const handleSelectAddon = useCallback((addon) => {
    if (!addon) return;
    setSelectedAddonKey(addon.key || '');
  }, []);

  const handleBackToTerms = useCallback(() => {
    setSelectedTermKey('');
    setSelectedAddonKey('');
  }, []);

  const handleBackToAddons = useCallback(() => {
    const addonCount = (selectedTerm?.addons || []).length;
    if (addonCount > 1) {
      setSelectedAddonKey('');
      return;
    }

    setSelectedTermKey('');
    setSelectedAddonKey('');
  }, [selectedTerm]);

  const handleUseSelectedAddon = useCallback(async () => {
    if (!selectedTerm || !selectedAddon) return;
    await handleProvisionAddon(selectedTerm.key, selectedAddon.key, selectedFieldKeys);
  }, [handleProvisionAddon, selectedFieldKeys, selectedTerm, selectedAddon]);

  const handleToggleSelectedField = useCallback((fieldKey) => {
    const normalizedFieldKey = String(fieldKey || '').trim();
    if (!normalizedFieldKey) return;

    setSelectedFieldKeys((prev) => {
      if (prev.includes(normalizedFieldKey)) {
        return prev.filter((entry) => entry !== normalizedFieldKey);
      }

      return [...prev, normalizedFieldKey];
    });
  }, []);

  return {
    query,
    setQuery,
    loading,
    error,
    terms: visibleTerms,
    selectedTerm,
    selectedAddon,
    isAddonStep,
    isAddonPreviewStep,
    isProvisioning,
    provisioningAddonKey,
    selectedFieldKeys,
    baseKey,
    handleSelectTerm,
    handleSelectAddon,
    handleBackToTerms,
    handleBackToAddons,
    handleToggleSelectedField,
    handleUseSelectedAddon,
  };
}
